import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { useAppAuth } from '@/auth/AppAuthProvider';
import { ARTICLES, PLAYBOOKS, GROUPS, COMMON_TASKS, FUNCTION_INDEX, searchHelp } from '../help/catalog';
import { DIAGRAMS } from '../help/diagrams';
import HelpDiagram from '../help/HelpDiagram';

export default function HelpCenter() {
  const { hasCapability } = useAppAuth();
  const [query, setQuery] = useState('');
  const [openArticles, setOpenArticles] = useState([]);
  const [target, setTarget] = useState(null);
  const results = useMemo(() => searchHelp(query), [query]);
  const jump = useCallback(id => {
    setQuery('');
    if (ARTICLES.some(article => article.id === id)) setOpenArticles(current => current.includes(id) ? current : [...current, id]);
    setTarget({ id });
  }, []);
  useEffect(() => {
    const followHash = () => {
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      if (id) jump(id);
    };
    followHash();
    window.addEventListener('hashchange', followHash);
    window.addEventListener('popstate', followHash);
    return () => { window.removeEventListener('hashchange', followHash); window.removeEventListener('popstate', followHash); };
  }, [jump]);
  useEffect(() => {
    if (!target) return undefined;
    const frame = requestAnimationFrame(() => {
      const element = document.getElementById(target.id);
      if (!element) return;
      const heading = element.querySelector('h2,h3') || element;
      heading.focus({ preventScroll: true });
      element.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [target]);
  const anchor = (id, text, { key, ...props } = {}) => <a key={key} href={`#${id}`} {...props} onClick={event => {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    window.history.pushState({}, '', `#${id}`);
    jump(id);
  }}>{text}</a>;
  const toggleArticle = id => setOpenArticles(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
  const workflowName = link => ({ '/port-calls': 'Port Calls', '/terminals': 'Berths & Terminal', '/assets': 'Terminal Assets', '/personnel': 'Labour & Personnel', '/labor-knowledge': 'Labour Knowledge' })[link];

  return <div className="help-center space-y-6" data-testid="help-center-page">
    <header>
      <p className="help-eyebrow">Operator & reviewer guide</p>
      <h1 className="font-heading text-3xl md:text-4xl font-bold">Help & Playbooks</h1>
      <p className="help-muted mt-2">Find your task, follow the steps, and check the result. Guidance reflects current application workflows.</p>
    </header>
    <div className="max-w-2xl">
      <label htmlFor="help-search" className="block font-medium mb-2">Search help</label>
      <Input id="help-search" aria-label="Search help" value={query} onChange={event => setQuery(event.target.value)} placeholder="Try approve port call, night plan, actual events, or chainage" />
    </div>
    {query.trim() ? <section className="panel p-4" aria-labelledby="help-results-heading" data-testid="manual-search-results">
      <div className="flex justify-between items-center gap-3"><h2 id="help-results-heading" className="font-bold" aria-live="polite">{results.length} result{results.length === 1 ? '' : 's'}</h2><button className="help-link" type="button" onClick={() => setQuery('')}>Clear search</button></div>
      {!results.length ? <p className="mt-3">No help articles match “{query}”. Try a task or field name, or clear search to browse all playbooks.</p> : <div className="grid md:grid-cols-2 gap-3 mt-3">
        {results.map(result => anchor(result.id, <><span className="help-eyebrow">{result.type}</span><strong className="block mt-1">{result.title}</strong><span className="help-muted block mt-1 text-sm">{result.summary}</span>{result.terms.length > 0 && <span className="help-muted block text-xs mt-2">{result.terms.join(' · ')}</span>}</>, { key: result.id, className: 'panel-2 p-3 help-result' }))}
      </div>}
    </section> : <>
      <section aria-labelledby="common-tasks-heading">
        <h2 id="common-tasks-heading" className="font-heading text-xl font-bold mb-3">What do you need to do?</h2>
        <nav className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" aria-label="Common tasks">
          {COMMON_TASKS.map(id => { const book = PLAYBOOKS.find(item => item.id === id); return anchor(id, book.title, { key: id, className: 'panel p-4 help-task-link' }); })}
        </nav>
      </section>
      <section className="panel p-4" aria-label="Getting started overview"><HelpDiagram diagram={DIAGRAMS.overview} />{anchor('getting-started', 'Read the getting started guide', { className: 'help-link' })}</section>
    </>}

    <section id="playbooks" className="help-destination" aria-labelledby="playbooks-heading">
      <h2 id="playbooks-heading" tabIndex={-1} className="font-heading text-2xl font-bold">Task playbooks</h2>
      <nav aria-label="Playbook categories" className="flex flex-wrap gap-3 my-4">{GROUPS.map((group, index) => anchor(`help-group-${index}`, group, { key: group, className: 'help-link' }))}</nav>
      {GROUPS.map((group, index) => <section key={group} id={`help-group-${index}`} className="help-destination mt-6" aria-labelledby={`group-heading-${index}`}>
        <h3 id={`group-heading-${index}`} tabIndex={-1} className="text-lg font-bold mb-3">{group}</h3>
        <div className="grid lg:grid-cols-2 gap-4">{PLAYBOOKS.filter(book => book.category === group).map(book => <article key={book.id} id={book.id} className="panel p-5 help-destination">
          <h3 tabIndex={-1} className="font-heading text-lg font-bold">{book.title}</h3>
          <p className="text-sm help-muted mt-2"><strong>Responsible role:</strong> {book.role}</p>
          <div className="mt-3 text-sm"><strong>Before you start</strong><p className="help-muted mt-1">{book.prerequisites}</p></div>
          <ol className="list-decimal pl-5 mt-4 space-y-2 text-sm">{book.steps.map(step => <li key={step}>{step}</li>)}</ol>
          <p className="help-done mt-4"><strong>Done when:</strong> {book.done}</p>
          <div className="flex flex-wrap justify-between gap-3 mt-4 text-sm">{anchor(book.section, 'Read the concept', { className: 'help-link' })}
            {hasCapability(book.capability) ? <Link to={book.link} className="help-link">Open {workflowName(book.link)}</Link> : <span className="help-muted">Ask the responsible role for access to this workflow.</span>}
          </div>
        </article>)}</div>
      </section>)}
    </section>

    <section aria-labelledby="reference-heading">
      <h2 id="reference-heading" className="font-heading text-2xl font-bold mb-3">Detailed reference & workflow diagrams</h2>
      <details className="panel p-4 mb-4"><summary className="font-bold cursor-pointer">Function index A–Z</summary><nav aria-label="Function index" className="flex flex-wrap gap-3 mt-3">{FUNCTION_INDEX.map(item => anchor(item.section, item.label, { key: item.label, className: 'help-link text-sm' }))}{anchor('outputs', 'Night Plan & Closeout', { className: 'help-link text-sm' })}{anchor('record-actuals', 'Actual events and services', { className: 'help-link text-sm' })}</nav></details>
      <nav aria-label="Reference topics" className="flex flex-wrap gap-x-4 gap-y-2 mb-4">{ARTICLES.map(article => anchor(article.id, article.title, { key: article.id, className: 'help-link text-sm' }))}</nav>
      <div className="space-y-3">{ARTICLES.map(article => {
        const open = openArticles.includes(article.id);
        return <article key={article.id} id={article.id} className="panel p-4 help-destination">
          <h2 tabIndex={-1} className="font-heading text-lg font-bold"><button type="button" className="help-disclosure" aria-expanded={open} aria-controls={`body-${article.id}`} onClick={() => toggleArticle(article.id)}>{article.title}<span aria-hidden="true">{open ? '−' : '+'}</span></button></h2>
          <p className="help-muted text-sm mt-1">{article.summary}</p>
          <div id={`body-${article.id}`} hidden={!open}>
            {open && <>
              {article.diagrams.filter(id => id !== 'overview').map(id => <HelpDiagram key={id} diagram={DIAGRAMS[id]} />)}
              <ol className="list-decimal pl-5 mt-4 space-y-3 text-sm leading-6">{article.body.map(paragraph => <li key={paragraph}>{paragraph}</li>)}</ol>
              {article.reference && <dl className="help-field-reference mt-4">{article.reference.map(([term, definition]) => <div key={term}><dt className="font-semibold">{term}</dt><dd className="help-muted">{definition}</dd></div>)}</dl>}
            </>}
          </div>
        </article>;
      })}</div>
    </section>
  </div>;
}
